import { toCsvText } from '../../../../utils/csv';
import type { ScholarshipPlayer } from '../../../../types/scholarships';
import { formatPlayerPercent } from './scholarshipPercent';
import { effectiveClassLabel } from './scholarshipClasses';
import { positionLabel, type RosterSport } from './positionGroups';

/**
 * Which optional columns the board is currently showing. The export mirrors the table rather
 * than dumping every field: a coach exporting a recruiting board does not want six empty money
 * columns, and one exporting a scholarship roster does not want two empty recruiting ones.
 */
export interface RosterCsvColumns {
  showUnits: boolean;
  showNil: boolean;
  showRevShare: boolean;
  showPackage: boolean;
  showScholarshipPercent: boolean;
  showRecruitingStatus: boolean;
}

const money = (value: number | null): string => (value == null ? '' : String(value));

/**
 * Builds the CSV text for a board's visible rows.
 *
 * Takes the ALREADY-FILTERED rows, so the export is what the coach is looking at rather than
 * the whole roster. Handing it the unfiltered list would produce a file that silently
 * disagrees with the screen it came from.
 */
/**
 * `sport` only changes how DH is spelled -- softball exports it as DP, matching what the coach
 * sees on the board. Optional and baseball-defaulted so the export is never the reason a caller
 * has to plumb sport through; the board passes its own.
 */
export const buildRosterCsv = (
  players: ScholarshipPlayer[],
  columns: RosterCsvColumns,
  sport: RosterSport = 'baseball',
): string => {
  const header = [
    'Name',
    'Position',
    'Secondary position',
    // Grad year for linked rows, eligibility for manual ones — the same two-facts-one-column
    // situation the board has, named here so a spreadsheet reader is not left guessing.
    'Class / grad year',
    'Bats',
    'Throws',
    ...(columns.showRecruitingStatus ? ['Offered', 'Committed'] : []),
    'Cost',
    'Award',
    ...(columns.showUnits ? ['Units'] : []),
    ...(columns.showNil ? ['NIL'] : []),
    ...(columns.showRevShare ? ['Rev share'] : []),
    ...(columns.showPackage ? ['Total package'] : []),
    ...(columns.showScholarshipPercent ? ['Scholarship %'] : []),
    'Tags',
    'Notes',
  ];

  const rows = players.map((p) => {
    const status = p.recruitingStatus ?? 'None';
    const isCommitted = status === 'Committed';
    const isOffered = status === 'Offered' || isCommitted;
    const totalPackage = (p.award ?? 0) + (p.nilAmount ?? 0) + (p.revShare ?? 0);

    return [
      `${p.firstName} ${p.lastName}`,
      p.position == null ? '' : positionLabel(p.position, sport),
      p.secondaryPosition == null ? '' : positionLabel(p.secondaryPosition, sport),
      effectiveClassLabel(p) ?? '',
      p.bats ?? '',
      p.throws ?? '',
      // Every row can carry a status now, manual rows included, so every row exports its
      // answer rather than a blank standing in for "cannot say".
      ...(columns.showRecruitingStatus
        ? [isOffered ? 'Yes' : 'No', isCommitted ? 'Yes' : 'No']
        : []),
      money(p.cost),
      money(p.award),
      ...(columns.showUnits ? [money(p.scholarshipUnit)] : []),
      ...(columns.showNil ? [money(p.nilAmount)] : []),
      ...(columns.showRevShare ? [money(p.revShare)] : []),
      ...(columns.showPackage ? [String(totalPackage)] : []),
      ...(columns.showScholarshipPercent ? [formatPlayerPercent(p).text] : []),
      p.tags.map((t) => `${t.tagName}: ${t.tagValue}`).join('; '),
      p.comments ?? '',
    ];
  });

  return toCsvText([header, ...rows]);
};

/** A filesystem-safe filename for a roster's export. */
export const rosterCsvFileName = (rosterName: string): string => {
  const safe = rosterName.replace(/[^a-zA-Z0-9 _-]/g, '').trim() || 'roster';
  return `${safe}.csv`;
};
